import type { Place, PlaceTranslation } from '../../types/place';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { selectCurrentLanguage } from '../../redux/selectors';
import { useTranslation } from 'react-i18next';
import { getChildren, createImageUrl } from '../../services/data';
import { getLangClass } from '../../services/langUtils';
import styles from './TodGallery.module.css';
import { useAsyncData } from '../../hooks/useAsyncData';

interface TodGalleryCardProps {
  place: Place;
  langClass: string;
  slug: string | undefined;
}

const TodGalleryCard: React.FC<TodGalleryCardProps> = ({ place, langClass, slug }) => {
  const title = place.translations?.[0]?.information_title || place.information_title || '';
  const description = place.translations?.[0]?.information_short_description
    || place.translations?.[0]?.information_description || '';
  const imageUrl = createImageUrl(place.information_photo);
  const href = `/${slug}/${place.id}`;

  return (
    <div className={styles['tod-gallery-card']}>
      <Link to={href}>
        <div className={styles['tod-gallery-image-gradient']}>
          <img src={imageUrl} alt={title} />
        </div>
      </Link>
      <div className={styles['tod-gallery-card-body']}>
        <div className={`${styles['tod-gallery-card-header']} ${langClass}`}>
          <p>{title}</p>
        </div>
        {description && (
          <p className={`${styles['tod-gallery-card-text']} ${langClass}`}>{description}</p>
        )}
      </div>
    </div>
  );
};

interface TodGalleryListProps {
  translations: PlaceTranslation[];
  childrenIds: (number | string)[];
}

const TodGalleryList: React.FC<TodGalleryListProps> = ({ translations, childrenIds }) => {
  const { i18n } = useTranslation();
  const { slug } = useParams();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const langClass = getLangClass(currentLanguage);

  const { data: children, isLoading } = useAsyncData<Place[]>(
    () => childrenIds?.length ? getChildren('Place', currentLanguage ?? 'en', childrenIds).then(r => r || []) : Promise.resolve([]),
    [childrenIds, currentLanguage]
  );

  return (
    <div className={styles['tod-galleries-container']} style={{ direction: i18n.dir() }}>
      <h1 className={langClass}>{i18n.t('pages.galleries-page.title', 'All Galleries')}</h1>
      <hr />
      <ul className={`${styles['tod-gallery-list']} ${langClass}`}>
        {(children ?? []).map((place, index) => (
          <li key={place.id || index}>
            <TodGalleryCard
              place={place}
              langClass={langClass}
              slug={slug}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TodGalleryList;
